// The job sheet's Photos (Amir, 24 Oct: the imported photos were on the jobs, but the sheet showed none):
// the job's photos, videos and documents from attachments.list, read after the sheet so they never hold
// it up. Riverstone draws Review's gallery, Blueprint ui-web's PhotoStrip; both show the first few with
// "Show all", open one full size in the viewer, and change who sees a photo for whoever reviews jobs.
// Over either, tools choose photos or videos, whose, and which first: Blueprint's are the library's
// GalleryTools (QA-743), Riverstone keeps its own (it has no component tokens).
// attachments.list reads what jobs.get reads (jobs.view, the job's own scope and place): a role that
// opens the sheet may read its photos, and one refused anyway sees no section rather than an error.

import { useEffect, useState } from 'react';
import { RpcError } from '@flowology/client';
import { photoFileName, photoNote } from '@flowology/domain';
import { PhotoStrip } from '@flowology/ui-web';
import { GalleryTools } from '@flowology/ui-web/fl';
import '@flowology/ui-web/base.css';
import { client } from '../api.ts';
import { errorText } from '../books/ui.tsx';
import { useDesign } from '../blueprint/design.ts';
import { GalleryTools as RiverstoneTools } from '../shared/GalleryTools.tsx';
import { ALL, galleryOf, type GalleryFilter } from '../shared/gallery-model.ts';
import { Loading } from '../shared/Loading.tsx';
import { MarkedUp } from '../shared/MarkedUp.tsx';
import { useMirror } from '../shared/mirror.ts';
import { namesOf, tileOf } from '../shared/photo-display-model.ts';
import { PhotoViewer } from '../shared/PhotoViewer.tsx';
import { PhotoVisibility } from '../shared/PhotoVisibility.tsx';
import { VideoWords } from '../shared/VideoWords.tsx';
import type { Attachment } from '../review/review-model.ts';
import {
  FIRST_PHOTOS,
  galleryToolsOf,
  jobFiles,
  photoRights,
  stripProps,
  viewableOf,
} from './photos-model.ts';

export function JobPhotos({
  jobId,
  jobNumber,
  held,
  timeZone,
}: {
  jobId: string;
  jobNumber: string;
  held: readonly string[];
  timeZone: string;
}) {
  const bp = useDesign() === 'blueprint';
  const rights = photoRights(held, useMirror());
  // null until attachments.list answers, so the sheet never says "No photos" before it knows
  const [files, setFiles] = useState<Attachment[] | null>(null);
  const [refused, setRefused] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [n, setN] = useState(0);
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<string | null>(null);
  const [filter, setFilter] = useState<GalleryFilter>(ALL);
  const [all, setAll] = useState(false);
  const [viewing, setViewing] = useState<{ list: Attachment[]; at: number } | null>(null);
  useEffect(() => {
    let live = true;
    client
      .call<{ attachments: Attachment[] }>('attachments.list', { jobId })
      .then((r) => {
        if (!live) return;
        setFiles(r.attachments);
        setError(null);
      })
      .catch((e: unknown) => {
        if (!live) return;
        if (e instanceof RpcError && (e.code === 'forbidden' || e.code === 'not_found')) setRefused(true);
        else setError(errorText(e, 'Could not load the photos.'));
      });
    return () => {
      live = false;
    };
  }, [jobId, n]);

  const share = async (id: string, shared: boolean) => {
    setBusy(true);
    setSaid(null);
    try {
      await client.call('attachments.setVisibility', {
        attachmentId: id,
        visibility: shared ? 'customer' : 'internal',
      });
      setSaid(shared ? 'Shown to the customer again.' : 'Hidden from the customer.');
      setN((x) => x + 1);
    } catch (e) {
      setSaid(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const caption = async (attachmentId: string, text: string | null) => {
    await client.call('attachments.setCaption', { attachmentId, caption: text });
    setN((x) => x + 1);
  };

  if (refused) return null;
  const { strip, others } = jobFiles(files ?? []);
  // the gallery's tools choose over the strip, in both designs
  const shown = galleryOf(strip, filter);
  const viewable = viewableOf(shown);
  // each one's note, else a made-up name numbered over the whole job, whatever is chosen (P-0161)
  const names = namesOf(files ?? []);
  const open = (id: string) => setViewing({ list: viewable, at: viewable.findIndex((a) => a.id === id) });
  const saidLine = said && (
    <p className={bp ? 'flw-sub' : 'small'} role="status">
      {said}
    </p>
  );
  const waiting = error ? (
    <p className="error">{error}</p>
  ) : (
    <Loading error={null} what="the photos" retry={() => setN((x) => x + 1)} />
  );
  const viewer = viewing && viewing.at >= 0 && (
    <PhotoViewer
      photos={viewing.list}
      start={viewing.at}
      timeZone={timeZone}
      onClose={() => setViewing(null)}
      {...(rights.caption ? { onCaption: caption } : {})}
    />
  );
  const title = `Photos${files ? ` (${strip.length})` : ''}`;
  const tools = strip.length > 1 && (
    <div className="jobphotos-tools">
      <GalleryTools value={filter} onChange={setFilter} {...galleryToolsOf(strip, filter)} />
    </div>
  );
  const data = bp && files ? stripProps(files, { jobNumber, timeZone, now: new Date() }, filter) : null;

  if (bp)
    return (
      <section className="job-sect bp-photos" aria-labelledby="job-photos-title">
        <h3 id="job-photos-title">{title}</h3>
        {!files ? (
          waiting
        ) : (
          <div className="flw">
            {saidLine}
            {tools}
            {/* none chosen: the tools say so, rather than the strip's "No photos on this job yet" */}
            {data && (data.photos.length > 0 || data.files.length > 0 || !strip.length) && (
              <PhotoStrip
                data={data}
                onOpen={open}
                busy={busy}
                {...(rights.share ? { onShare: (id: string, s: boolean) => void share(id, s) } : {})}
              />
            )}
          </div>
        )}
        {viewer}
      </section>
    );

  return (
    <section className="job-sect job-photos" aria-labelledby="job-photos-title">
      <h3 id="job-photos-title">{title}</h3>
      {!files ? (
        waiting
      ) : !strip.length && !others.length ? (
        <p className="muted">No photos on this job.</p>
      ) : (
        <>
          {saidLine}
          {strip.length > 1 && <RiverstoneTools items={strip} value={filter} onChange={setFilter} />}
          {/* Review's gallery (review/Review.tsx PhotoGrid), the first few at first */}
          <ul className="photo-grid">
            {(all ? shown : shown.slice(0, FIRST_PHOTOS)).map((p) => {
              const tile = tileOf(p);
              const words = photoNote(p.caption);
              return (
                <li key={p.id} className="pg-tile">
                  {tile.t === 'picture' || tile.t === 'video' ? (
                    <button
                      className="pg-open"
                      aria-label={`Open ${words ?? `the ${p.category} ${p.kind === 'video' ? 'video' : 'photo'}`} full size`}
                      onClick={() => open(p.id)}
                    >
                      {tile.t === 'video' ? (
                        <span className="ph small">▶ Video</span>
                      ) : (
                        <img src={tile.src} alt="" loading="lazy" />
                      )}
                    </button>
                  ) : tile.t === 'file' ? (
                    <a className="ph small" href={tile.href ?? undefined} target="_blank" rel="noreferrer">
                      {tile.words}
                    </a>
                  ) : (
                    <span className="ph muted small" title={p.failedReason ?? undefined}>
                      {tile.t === 'waiting' && p.kind !== 'video' ? tile.words : <VideoWords p={p} />}
                    </span>
                  )}
                  <span className="small muted pg-caption" title={photoFileName(p.caption) ?? undefined}>
                    {names.get(p.id)}
                  </span>
                  <MarkedUp p={p} />
                  {p.kind === 'photo' && (
                    <PhotoVisibility
                      p={p}
                      busy={busy}
                      {...(rights.share
                        ? { onSet: (v: 'customer' | 'internal') => void share(p.id, v === 'customer') }
                        : {})}
                    />
                  )}
                </li>
              );
            })}
          </ul>
          {!all && shown.length > FIRST_PHOTOS && (
            <button type="button" className="btn quiet sm" onClick={() => setAll(true)}>
              Show all {shown.length}
            </button>
          )}
          {/* kept with the job, out of the gallery: a message's wrapper, a contact card */}
          {others.length > 0 && (
            <details className="small">
              <summary>Other files ({others.length})</summary>
              <ul>
                {others.map((p) => (
                  <li key={p.id}>
                    {p.url ? (
                      <a
                        href={p.url}
                        target="_blank"
                        rel="noreferrer"
                        title={photoFileName(p.caption) ?? undefined}
                      >
                        {names.get(p.id) ?? 'A file'}
                      </a>
                    ) : (
                      (names.get(p.id) ?? 'A file')
                    )}
                    <span className="muted"> · {p.contentType}</span>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </>
      )}
      {viewer}
    </section>
  );
}
